import * as React from "react"
import { cva } from "class-variance-authority";

import { cn } from "@/lib/utils"

/**
 * Badge Component
 * 
 * A flexible, reusable badge component for displaying labels, statuses, and tags.
 * Supports multiple variants, sizes, and can be made interactive with onClick.
 * 
 * This is a foundational UI component used throughout the app for:
 * - Community badges
 * - Status pills
 * - Verification indicators
 * - Category labels
 * - And more
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Badge content (text, icons, etc.)
 * @param {string} [props.variant] - Visual variant:
 *   - 'default' - Blue (primary semantic color)
 *   - 'success' - Green (verified, completed)
 *   - 'warning' - Amber (pending, caution)
 *   - 'error' - Red (errors, critical)
 *   - 'info' - Sky blue (informational)
 *   - 'outline' - White with gray border (subtle)
 *   - 'frosted' - Translucent white (for dark/image backgrounds)
 *   - 'purple' - Purple (brand color, featured)
 *   - 'gray' - Gray (neutral, secondary)
 *   - 'secondary' - Legacy variant (backward compatibility)
 *   - 'destructive' - Legacy variant (backward compatibility)
 * @param {string} [props.size] - Size variant:
 *   - 'xs' - Extra small (text-xs, compact padding)
 *   - 'sm' - Small (text-xs, standard padding)
 *   - 'md' - Medium (text-sm, default)
 *   - 'lg' - Large (text-base, generous padding)
 * @param {string} [props.rounded] - Border radius:
 *   - 'sm' - Small radius (rounded)
 *   - 'md' - Medium radius (rounded-lg)
 *   - 'full' - Full radius (rounded-full, default)
 * @param {string} [props.className] - Additional CSS classes
 * @param {Function} [props.onClick] - Click handler (makes badge interactive with proper a11y)
 * 
 * @example
 * // Simple text badge
 * <Badge>Default Badge</Badge>
 * 
 * @example
 * // Success badge with icon
 * <Badge variant="success" size="sm">
 *   <CheckCircleIcon className="w-3 h-3" />
 *   Verified
 * </Badge>
 * 
 * @example
 * // Frosted badge for hero images
 * <Badge variant="frosted">
 *   ⭐ 4.8 (39 reviews)
 * </Badge>
 * 
 * @example
 * // Clickable badge
 * <Badge variant="outline" onClick={() => handleClick()}>
 *   Click me
 * </Badge>
 */
const badgeVariants = cva(
  "inline-flex items-center font-medium border transition-all focus:outline-none focus:ring-2 focus:ring-offset-2",
  {
    variants: {
      variant: {
        // Default blue variant (primary semantic color)
        default:
          "bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200 focus:ring-blue-500",
        
        // Legacy variants (maintained for backward compatibility)
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80 focus:ring-ring",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80 focus:ring-ring",
        
        // Semantic variants
        success: "bg-green-100 text-green-700 border-green-200 hover:bg-green-200 focus:ring-green-500",
        warning: "bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200 focus:ring-amber-500",
        error: "bg-red-100 text-red-700 border-red-200 hover:bg-red-200 focus:ring-red-500",
        info: "bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-200 focus:ring-sky-500",
        outline: "bg-white text-gray-700 border-gray-300 hover:bg-gray-50 focus:ring-gray-500",
        frosted: "bg-white/20 text-white border-white/30 backdrop-blur-sm hover:bg-white/30 focus:ring-white/50",
        purple: "bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200 focus:ring-purple-500",
        gray: "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200 focus:ring-gray-500",
      },
      size: {
        xs: "text-xs px-2 py-0.5 gap-1",
        sm: "text-xs px-2 py-1 gap-1",
        md: "text-sm px-3 py-1.5 gap-1.5",
        lg: "text-base px-4 py-2 gap-2",
      },
      rounded: {
        sm: "rounded",
        md: "rounded-lg",
        full: "rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      rounded: "full",
    },
  }
)

function Badge({
  children,
  className,
  variant,
  size,
  rounded,
  onClick,
  ...props
}) {
  const isInteractive = Boolean(onClick);
  
  return (
    <span
      className={cn(
        badgeVariants({ variant, size, rounded }),
        isInteractive && "cursor-pointer hover:scale-105",
        className
      )}
      onClick={onClick}
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={isInteractive ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
      {...props}
    >
      {children}
    </span>
  );
}

export { Badge, badgeVariants }